// conflict-apply.js — the EDITOR-COUPLED shells over the conflict-detect floor.
//
// resolveInboundConflict (notes/conflict-detect.js) is PURE; the decision +
// durable-storage half lives in conflict-apply-core.js (editor-free). This file
// holds the thin OPEN-NOTE shell (applyInboundConflict) that adds the editor /
// baseline / banner side-effects on top of the CoreResult, plus useTheirs. The
// PowerSync path (core/powersync-bootstrap.js) and the IndexedDB / cross-window
// path (core/app-init.js) both call applyInboundConflict, so they share exactly
// one code path — which is what resolves the historic trim() drift.
//
// The caller does the path-specific work (fetching `remote`, owning the loadNote
// generation counter) and hands us a `recheckGen` predicate so we preserve the
// gen-guards: the core re-checks generation before any write, and the shell
// skips its editor mutation when the core reports it aborted. We never touch the
// file list directly (that would form a note-manager ↔ file-list import cycle);
// instead we return a flag telling the caller whether to rebuild it.

import {
  clearPendingConflict,
  getCurrentFileName,
  reflectActiveView,
  setLastRemoteContent,
  setLastSavedContent,
  setPendingConflict,
} from '../core/app-state.js';
import { NoteEditor } from '../editor/note-editor.js';
import { showConflictBanner } from '../ui/conflict-banner.js';
import { updateStatus } from '../ui/status-pill.js';
import { applyInboundConflictCore } from './conflict-apply-core.js';
import type { ApplyInboundConflictArgs } from './conflict-detect.js';

// Re-export the merge-engine test seam from its new home so existing importers
// (the merge suite) keep their `from './conflict-apply.js'` path.
export { setMergeEngineLoaderForTest } from './conflict-apply-core.js';

/**
 * Classify + apply an inbound remote update for the currently-open note. Calls
 * the editor-free core for the decision + storage half, then performs the
 * editor / baseline / banner side-effects on the returned CoreResult.
 *
 * @param {object} args
 * @param {string}  args.name        Open note display name.
 * @param {?string} args.base        getLastRemoteContent() — the 3-way base.
 * @param {string}  args.local       Live editor content.
 * @param {string}  args.remote      Inbound remote content.
 * @param {?string} args.saved       getLastSavedContent().
 * @param {boolean} args.hasUnsavedEdits
 * @param {any}     args.NS          Active NoteStorage (window.NoteStorage / IDB).
 * @param {() => boolean} [args.recheckGen]  Returns true if a newer loadNote
 *        superseded us; we bail before any editor mutation when it does.
 * @returns {Promise<{ action: string, contentChanged: boolean,
 *                      structuralChanged: boolean }>}
 *          action is one of ignore/fastForward/rebase/merge/fork. 'merge' is the
 *          auto-merge upgrade of the divergence case: when provably safe the core
 *          wrote the merged text and we replace the editor + advance all three
 *          baselines + toast. Anything unsafe falls through to the unchanged
 *          'fork'.
 */
export async function applyInboundConflict({
  name,
  base,
  local,
  remote,
  saved,
  hasUnsavedEdits,
  NS,
  recheckGen,
}: ApplyInboundConflictArgs) {
  const stale = typeof recheckGen === 'function' ? recheckGen : () => false;

  const result = await applyInboundConflictCore({
    name,
    base,
    local,
    remote,
    saved,
    hasUnsavedEdits,
    NS,
    recheckGen,
  });

  switch (result.action) {
    case 'ignore':
      return { action: 'ignore', contentChanged: false, structuralChanged: false };

    case 'fastForward':
      // Re-check generation before mutating the editor / baselines — the user may
      // have navigated to a different note during the async classify.
      if (stale()) {
        return { action: 'fastForward', contentChanged: false, structuralChanged: false };
      }
      // The existing safe replace: editor takes remote, both baselines advance.
      NoteEditor.replaceAll(remote, undefined, { remote: true });
      setLastSavedContent(remote);
      setLastRemoteContent(remote);
      reflectActiveView();
      updateStatus('Note updated from another device.', true);
      return { action: 'fastForward', contentChanged: true, structuralChanged: false };

    case 'rebase':
      // rebase: both sides already converged on the same text. Don't touch the
      // editor (it's already showing `remote === local`); just advance the
      // baselines so future diffs are measured from here. No banner.
      if (stale()) {
        return { action: 'rebase', contentChanged: false, structuralChanged: false };
      }
      setLastSavedContent(remote);
      setLastRemoteContent(remote);
      return { action: 'rebase', contentChanged: true, structuralChanged: false };

    case 'merge':
      // The core wrote NS.setNote(name, merged) already (unless it aborted on a
      // stale generation). Mirror the pre-split behaviour: skip the editor
      // mutation when the core aborted.
      if (result.staleAborted) {
        return { action: 'merge', contentChanged: false, structuralChanged: false };
      }
      NoteEditor.replaceAll(result.merged, undefined, { remote: true });
      setLastSavedContent(result.merged);
      setLastRemoteContent(result.merged);
      reflectActiveView();
      updateStatus('Merged remote changes.', true);
      return { action: 'merge', contentChanged: true, structuralChanged: false };

    default: {
      // fork. Keep local on screen (it keeps uploading via autosave/LWW because
      // we deliberately do NOT setLastSavedContent); the core wrote the sidecar.
      if (result.staleAborted) {
        return { action: 'fork', contentChanged: false, structuralChanged: false };
      }
      // Advance the remote baseline so a re-delivery of the SAME remote is an
      // echo (-> 'ignore') and can't re-fork. Do NOT advance the saved baseline.
      setLastRemoteContent(remote);
      // Per-note pending-conflict flag drives the banner + guards re-forking.
      setPendingConflict(name, { remote, forkName: result.forkName });
      showConflictBanner(name, { remote, forkName: result.forkName });
      // A new sidecar note appeared → the caller should rebuild the file list.
      return { action: 'fork', contentChanged: false, structuralChanged: result.wroteFork };
    }
  }
}

// The "Use theirs" banner action. Replaces the editor with the remote version,
// advances BOTH baselines (the user explicitly chose remote, so local stops
// uploading), and clears the pending flag. Guards against the user having
// navigated to a different note since the banner appeared. status-pill.js
// invokes this via the window._conflictUseTheirs runtime callback so it can
// stay a leaf module (no static NoteEditor import).
/**
 * @param {string} name
 * @param {any} remote - the remote content to accept.
 * @returns {void}
 */
export function useTheirs(name: any, remote: any) {
  if (getCurrentFileName() !== name) {
    // Editor moved on — just drop the pending flag; nothing to overwrite.
    clearPendingConflict(name);
    return;
  }
  NoteEditor.replaceAll(remote, undefined, { remote: true });
  setLastSavedContent(remote);
  setLastRemoteContent(remote);
  reflectActiveView();
  clearPendingConflict(name);
}

// Register the runtime callback for conflict-banner.js (cycle-break: the banner
// is a leaf and must not statically import NoteEditor). Idempotent.
if (typeof window !== 'undefined') {
  window._conflictUseTheirs = useTheirs;
}
